import { Paper, Stack, Switch, Typography } from '@mui/material'
import ReassignExamDetailsList from './ReassignExamDetailsList'
import { useState, useEffect } from 'react'
import { ContainerParam } from '@features/subject/subject.types'
import { useFormContext } from 'react-hook-form'
import { ExamReassignementFormValues } from '../../schemas/ExamReassignementEsignValidationSchema'

export default function SelectExams({ containers }: { containers?: ContainerParam[] }) {
  // Get form methods from context
  const { setValue, getValues } = useFormContext<ExamReassignementFormValues>()

  // State to track selected exam IDs
  const [selectAll, setSelectAll] = useState(false)

  // Get all available exam IDs from containers
  const getAllExamIds = (): string[] => {
    const allIds: string[] = []
    containers?.forEach((container) => {
      container.examMetadata?.forEach((exam) => {
        if (exam.exam_metadata_id) {
          allIds.push(exam.exam_metadata_id)
        }
      })
    })
    return allIds
  }

  // Handle "select all" toggle
  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    setSelectAll(checked)

    if (checked) {
      // Select all exams
      const allIds = getAllExamIds()
      setValue('exam_metadata_ids', allIds, { shouldValidate: true })
    } else {
      // Deselect all exams
      setValue('exam_metadata_ids', [], { shouldValidate: true })
    }
  }

  // Handle individual exam selection
  const handleExamSelection = (examId: string, isSelected: boolean) => {
    const currentSelectedExams = getValues('exam_metadata_ids')
    let updatedSelection: string[]

    if (isSelected) {
      updatedSelection = [...currentSelectedExams, examId]
    } else {
      updatedSelection = currentSelectedExams.filter((id) => id !== examId)
    }

    setValue('exam_metadata_ids', updatedSelection, { shouldValidate: true })

    // Update selectAll state based on whether all exams are selected
    const allExamIds = getAllExamIds()
    setSelectAll(allExamIds.length > 0 && allExamIds.every((id) => updatedSelection.includes(id)))
  }

  // Check which exams are currently selected
  const isExamSelected = (examId: string): boolean => {
    return getValues('exam_metadata_ids').includes(examId)
  }

  // Check the initial state of selectAll when containers change
  useEffect(() => {
    const allIds = getAllExamIds()
    const currentSelectedExams = getValues('exam_metadata_ids')

    if (allIds.length > 0 && allIds.every((id) => currentSelectedExams.includes(id))) {
      setSelectAll(true)
    } else {
      setSelectAll(false)
    }
  }, [containers, getValues])

  return (
    <Stack spacing={4} width={840}>
      <Stack>
        <Typography color="text.primary" variant="h4">
          Select exams to reassign
        </Typography>
        <Typography color="text.secondary" variant="body1">
          You can select one or multiple exams for reassignment
        </Typography>
      </Stack>
      <Paper
        sx={{
          borderRadius: 3,
          boxShadow: 1,
        }}
      >
        <Stack padding={4} spacing={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography color="text.primary" variant="h6">
              Exams
            </Typography>
            <Stack direction="row" spacing={1}>
              <Typography color="text.primary" variant="body1">
                Reassign all exams
              </Typography>
              <Switch size="small" checked={selectAll} onChange={handleSelectAllChange} name="select-all-exams" />
            </Stack>
          </Stack>
          <Stack spacing={4}>
            {containers?.map((container, index) => (
              <Stack key={index} spacing={3}>
                <Typography color="text.primary" variant="subtitle1">
                  {container?.name}
                </Typography>
                <ReassignExamDetailsList
                  container={container}
                  isExamSelected={isExamSelected}
                  onExamSelection={handleExamSelection}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  )
}
